import {CompactCallRenderer} from '@shared/components/compact-call-view/compact-call-view.model';
import {CallVideoSize} from './call-media.port';
import {CallService} from './call.service';

/** A player this renderer drew, and whose. */
interface DrawnPlayer {
  identity: string;
  player: HTMLElement;
}

/**
 * The compact call view's renderer for an ITP call (ITP-E-185-01): it draws through the call's own media — the CallService
 * over the CallMediaPort, Zoom's adapter in the app — so the view never owns a client and there is one per page. Each draw
 * asks the media for a new player and puts it in the element the view gave; each drop takes it out and hands it back.
 * A shared screen is drawn into the view's own canvas, as Meet draws it (ITP-E-189-05): the media keeps it drawn there.
 */
export class CallMediaRenderer implements CompactCallRenderer {
  /** What was drawn, by the element it was drawn into. */
  private readonly drawn = new Map<HTMLElement, DrawnPlayer>();
  /** The canvases a shared screen is drawn into. */
  private readonly shares = new Set<HTMLCanvasElement>();

  constructor(private callService: CallService) {
  }

  async attachVideo(identity: string, el: HTMLElement, size: CallVideoSize): Promise<boolean> {
    if (this.drawn.has(el)) {
      await this.drop(el);
    }
    const player = await this.callService.attachVideo(identity, size);
    return this.place(el, {identity, player: player as HTMLElement});
  }

  detachVideo(identity: string, el: HTMLElement): Promise<void> {
    return this.drawn.get(el)?.identity === identity ? this.drop(el) : Promise.resolve();
  }

  async attachShare(el: HTMLElement, identity: string): Promise<boolean> {
    if (!(el instanceof HTMLCanvasElement) || !await this.callService.showShare(el, identity)) {
      return false;
    }
    this.shares.add(el);
    return true;
  }

  detachShare(el: HTMLElement): Promise<void> {
    if (!(el instanceof HTMLCanvasElement) || !this.shares.delete(el)) {
      return Promise.resolve();
    }
    return this.callService.hideShare(el);
  }

  /** Every player still drawn goes back: the host that made this renderer is going. */
  async dispose(): Promise<void> {
    for (const el of [...this.drawn.keys()]) {
      await this.drop(el);
    }
    for (const canvas of [...this.shares]) {
      await this.detachShare(canvas);
    }
  }

  private place(el: HTMLElement, drawn: DrawnPlayer): boolean {
    if (!drawn.player) {
      return false;
    }
    el.appendChild(drawn.player);
    this.drawn.set(el, drawn);
    return true;
  }

  private drop(el: HTMLElement): Promise<void> {
    const drawn = this.drawn.get(el);
    if (!drawn) {
      return Promise.resolve();
    }
    this.drawn.delete(el);
    drawn.player.remove();
    return this.callService.detachVideo(drawn.identity, drawn.player);
  }
}
